import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { appConfig } from '../config';
import { FLUSH_AT, MAX_BATCH, viaFor, type UsageEvent } from '../utils/usageEvents';

/**
 * Silent usage log: which buttons each person taps and which screens they reach,
 * so navigation can be redesigned from real use (spec:
 * docs/superpowers/specs/2026-10-03-usage-tracking-design.md). Nothing reads it in
 * the app — scripts/usage-report.cjs pulls it for analysis.
 *
 * Module-level like errorLog.ts: the click listener fires on every tap, so it must
 * not touch React state. Never throws — a lost batch is fine, a broken tap is not.
 */

export interface UsageUser {
  uid: string; // Firebase Auth UID — firestore.rules requires it to match the writer
  userId: string; // users/{id}
  role: string;
  jobTitle: string | null;
}

let user: UsageUser | null = null;
let paused = false;
let buffer: UsageEvent[] = [];
let screen = 'none';
let lastTap: { target: string; t: number } | null = null;

/**
 * The last few taps and screen changes, kept in memory only — errorLog.ts attaches them to an
 * error so the Error Log shows what the person was doing just before it. Kept even when the
 * usage log itself isn't recording (View As), since it never leaves the phone on its own.
 */
const TRAIL_MAX = 12;
let trail: { t: number; what: string }[] = [];
const remember = (t: number, what: string): void => {
  trail.push({ t, what });
  if (trail.length > TRAIL_MAX) trail = trail.slice(-TRAIL_MAX);
};

/** Oldest first, e.g. "45s before: opened job_detail (via Open Job)", "3s before: tapped Create AI Invoice on job_detail". */
export const recentActivity = (now: number = Date.now()): string[] =>
  trail.map(({ t, what }) => `${Math.max(0, Math.round((now - t) / 1000))}s before: ${what}`);

const recording = (): boolean => !import.meta.env.DEV && !!db && !!user && !paused;

const push = (event: UsageEvent): void => {
  if (!recording()) return;
  buffer.push(event);
  if (buffer.length >= FLUSH_AT) flushUsage();
};

export const flushUsage = (): void => {
  if (!db || !user) {
    buffer = [];
    return;
  }
  while (buffer.length > 0) {
    const events = buffer.splice(0, MAX_BATCH);
    addDoc(collection(db, 'usageEvents'), {
      uid: user.uid,
      userId: user.userId,
      role: user.role,
      jobTitle: user.jobTitle,
      version: appConfig.version,
      createdAt: serverTimestamp(),
      events,
    }).catch(() => {
      // Intentionally silent — usage data is best-effort
    });
  }
};

/** Sign-in, sign-out, or account switch. Pending taps are written under whoever made them. */
export const setUsageUser = (next: UsageUser | null): void => {
  if (user && next?.uid !== user.uid) flushUsage();
  user = next;
  if (!next) buffer = [];
};

/** View As: pause so an admin's look-through isn't counted as anyone's real use. */
export const setUsagePaused = (next: boolean): void => {
  if (next && !paused) flushUsage();
  paused = next;
  if (next) lastTap = null;
};

export const trackTap = (target: string, now: number = Date.now()): void => {
  remember(now, `tapped ${target} on ${screen}`);
  if (!recording()) return;
  lastTap = { target, t: now };
  push({ t: now, type: 'tap', screen, target });
};

export const trackScreen = (next: string, now: number = Date.now()): void => {
  const from = screen;
  screen = next;
  if (next === from) return;
  remember(now, `opened ${next}`);
  push({ t: now, type: 'screen', screen: next, from, via: viaFor(lastTap, now) });
};

export const trackOpen = (
  info: { bottomTabs: string[]; tabsCustomized: boolean; standalone: boolean; wide: boolean },
  now: number = Date.now(),
): void => push({ t: now, type: 'open', screen, ...info });

export const trackResume = (now: number = Date.now()): void => push({ t: now, type: 'resume', screen });

export const _resetUsageForTests = (): void => {
  user = null;
  paused = false;
  buffer = [];
  screen = 'none';
  lastTap = null;
  trail = [];
};
